import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Analyse, ScoreMarket } from 'r/types';
import { captureException, showNotification } from 'r/utils';
import {
  fetchApiRaw,
  DELETE_MARKET_SCORE,
  POST_MARKET_SCORE,
  PUT_MARKET_SCORE,
} from 'r/utils/fetchApiRaw';
import { StoredAuth } from 'r/utils/Stored';
import {
  getFrozenMismatches,
  getScoreError,
  isMarketFrozen,
  marketBodyFromAnalysis,
  titleForSymbol,
  withoutFrozenFields,
  type ScoreMarketBody,
} from 'r/utils/scoreMarket';

const SCORE_ERRORS: Record<string, string> = {
  MARKET_ALREADY_STARTED: 'Market has already started',
  MARKET_HAS_PREDICTIONS:
    'Market has predictions: KOL, pair, direction, tweet and dates are locked',
  MARKET_NOT_FOUND: 'Market not found',
  MARKET_ALREADY_EXISTS: 'This analysis already has a market',
  PAIR_NOT_ALLOWED: 'Pair is not allowed on Score (must trade on Hyperliquid)',
  PAIR_PRICE_UNAVAILABLE: 'Pair has no reliable price right now',
  WRONG_DATES: 'Invalid dates (start in the past or wrong order)',
  WRONG_KOL: 'KOL data is incomplete (id, username, name or avatar)',
  ACCESS_DENIED: 'Your account is not a CMS admin',
};

export const notifyScoreError = (e: unknown, fallback: string) => {
  captureException(e);
  const { code } = getScoreError(e);
  showNotification('error', SCORE_ERRORS[code] ?? `${fallback} (${code})`);
};

export function useCreateScoreMarket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: ScoreMarketBody) =>
      (await fetchApiRaw(
        POST_MARKET_SCORE,
        { body },
        StoredAuth.getAll().cms,
      )) as ScoreMarket,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['score-markets'] });
    },
  });
}

export function useUpdateScoreMarket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      marketId,
      body,
    }: {
      marketId: string;
      body: ScoreMarketBody;
    }) =>
      (await fetchApiRaw(
        PUT_MARKET_SCORE,
        { urlParams: [marketId], body },
        StoredAuth.getAll().cms,
      )) as ScoreMarket,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['score-markets'] });
    },
  });
}

export function useDeleteScoreMarket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (marketId: string) => {
      await fetchApiRaw(
        DELETE_MARKET_SCORE,
        { urlParams: [marketId] },
        StoredAuth.getAll().cms,
      );
    },
    onSuccess: () => {
      showNotification('success', 'Market deleted from Score');
      queryClient.invalidateQueries({ queryKey: ['score-markets'] });
    },
    onError: (e) => notifyScoreError(e, 'Failed to delete market'),
  });
}

// Pushes the analysis fields (KOL, pair, direction, tweet) to its market.
// A market with predictions only takes the texts; locked fields are left out.
// A pair change also renames the cashtag in the title.
export function useSendAnalysisToScore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      analysis,
      market,
    }: {
      analysis: Analyse;
      market: ScoreMarket;
    }) => {
      await fetchApiRaw(
        PUT_MARKET_SCORE,
        {
          urlParams: [market.id],
          body:
            isMarketFrozen(market) ?
              withoutFrozenFields(marketBodyFromAnalysis(analysis), market)
            : {
                ...marketBodyFromAnalysis(analysis),
                ...titleForSymbol(market, analysis.pair),
              },
        },
        StoredAuth.getAll().cms,
      );
    },
    onSuccess: (_data, { analysis, market }) => {
      const skipped =
        isMarketFrozen(market) ? getFrozenMismatches(analysis, market) : [];
      if (skipped.length > 0) {
        showNotification(
          'error',
          `Market has predictions: ${skipped.join(', ')} not updated. KOL profile and tweet text sent.`,
        );
      } else {
        showNotification('success', 'Update sent to Score');
      }
      queryClient.invalidateQueries({ queryKey: ['score-markets'] });
    },
    onError: (e) => notifyScoreError(e, 'Failed to send update to Score'),
  });
}
